import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useRef, type FormEvent } from "react";
import { Page, SectionEyebrow } from "@/components/SiteLayout";
import hijZegtKom from "@/assets/hij_zegt_kom.mp4.asset.json";
import emailjs from "@emailjs/browser";

export const Route = createFileRoute("/inschrijven")({
  head: () => ({
    meta: [
      { title: "Inschrijven — Hi Coach" },
      { name: "description", content: "Schrijf je kind in bij Hi Coach. Vul de gegevens in en wij zorgen dat alles klaarstaat voor de eerste zondagochtend." },
      { property: "og:title", content: "Inschrijven bij Hi Coach" },
      { property: "og:description", content: "Jouw kind doet mee." },
      { property: "og:url", content: "/inschrijven" },
    ],
    links: [{ rel: "canonical", href: "/inschrijven" }],
  }),
  component: Inschrijven,
});

const inputCls = "w-full px-4 py-3 rounded-xl border-2 border-border bg-white font-medium focus:outline-none focus:border-rb-blue transition-colors";


const PAKKETTEN = [
  { v: "Hi Instap", d: "€50,- per maand (2 termijnen, 1 thema van 7 lessen)", c: "#FF0000" },
  { v: "Hi Sprong", d: "€45,- per maand (4 termijnen, 2 thema's van 14 lessen)", c: "#FFD700" },
  { v: "Hi Groei", d: "€42,50 per maand (6 termijnen, 3 thema's van 21 lessen)", c: "#FF7F00" },
  { v: "Hi Held", d: "€40,- per maand (10 termijnen, alle 5 de thema's inclusief diploma-uitreiking)", c: "#1E90FF" },
];
const MATEN = ["Maat 92/98", "Maat 104/110", "Maat 116/122"];

function InschrijvenContent() {
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only UI-driven state (radio/checkbox) — no state per text keystroke
  const [pakket, setPakket] = useState("Hi Groei");
  const [maat, setMaat] = useState("Maat 104/110");
  const submittedName = useRef("");

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (sending) return;
    setError(null);
    setSending(true);
    const data = new FormData(e.currentTarget);
    const voornaam = data.get("voornaam") as string;
    const achternaam = data.get("achternaam") as string;
    submittedName.current = voornaam;
    try {
      const email = data.get("email") as string;
      // Bevestiging naar klant
      await emailjs.send(
        "service_gnizfwq",
        "template_ayyj4n2",
        { name: `${voornaam} ${achternaam}`, email, voornaam },
        "2BUPqpQPgvYkZBETf",
      );
      // Notificatie naar info@hicoachclub.nl
      await emailjs.send(
        "service_gnizfwq",
        "template_qz61uj6",
        {
          voornaam,
          achternaam,
          email,
          telefoon: data.get("telefoon"),
          adres: `${data.get("straat")} ${data.get("huisnummer")}, ${data.get("postcode")} ${data.get("woonplaats")}`,
          kindVoornaam: data.get("kindVoornaam"),
          geboortedatum: data.get("geboortedatum"),
          pakket,
          maat,
        },
        "2BUPqpQPgvYkZBETf",
      );
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      setSubmitted(true);
    } catch {
      setError("Geen verbinding. Controleer je internet en probeer het opnieuw.");
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <div className="text-6xl">🎉</div>
          <h1 className="mt-6 text-4xl font-extrabold text-gradient-rainbow">Inschrijving ontvangen!</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            We nemen zo spoedig mogelijk persoonlijk contact met je op.
          </p>
          <Link to="/" className="btn-cta mt-8 inline-flex">Terug naar home</Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="pt-24 pb-10 bg-white">
        <div className="mx-auto max-w-3xl px-6">
          <SectionEyebrow color="var(--rb-green)">Inschrijven</SectionEyebrow>
          <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
            Jouw kind <span style={{ color: "var(--rb-green)" }}>doet mee</span>
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Wil je eerst vrijblijvend kennismaken?{" "}
            <Link to="/inschrijven" className="underline font-bold">Vraag een gratis proefles aan.</Link>
          </p>
          <p className="mt-6 text-lg">
            Goed dat je er bent. Vul hieronder de gegevens in en wij zorgen dat alles klaarstaat voor de eerste zondagochtend.
          </p>
        </div>
      </section>

      <section className="pb-20">
        <form onSubmit={submit} className="mx-auto max-w-3xl px-6 space-y-10">
          <div>
            <h2 className="text-xl font-extrabold mb-4" style={{ color: "var(--rb-red)" }}>Jouw gegevens</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required name="voornaam" placeholder="Voornaam" className={inputCls} />
              <input required name="achternaam" placeholder="Achternaam" className={inputCls} />
              <input required name="email" type="email" placeholder="E-mailadres" className={inputCls} />
              <input required name="telefoon" type="tel" placeholder="Telefoonnummer" className={inputCls} />
              <input required name="straat" placeholder="Straatnaam" className={`${inputCls} sm:col-span-2`} />
              <input required name="huisnummer" placeholder="Huisnummer" className={inputCls} />
              <input required name="postcode" placeholder="Postcode" className={inputCls} />
              <input required name="woonplaats" placeholder="Woonplaats" className={`${inputCls} sm:col-span-2`} />
            </div>
          </div>

          <div>
            <h2 className="text-xl font-extrabold mb-4" style={{ color: "var(--rb-blue)" }}>Gegevens van je kind</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <input required name="kindVoornaam" placeholder="Voornaam kind" className={inputCls} />
              <input required name="geboortedatum" type="date" className={inputCls} />
            </div>
          </div>

          <div>
            <h2 className="text-xl font-extrabold mb-4" style={{ color: "var(--rb-orange)" }}>Kies je pakket</h2>
            <div className="grid gap-3">
              {PAKKETTEN.map((p) => {
                const active = pakket === p.v;
                return (
                  <label key={p.v} className={`cursor-pointer rounded-2xl border-2 p-5 flex items-start gap-4 transition-all ${active ? "shadow-lg" : "hover:bg-secondary"}`} style={{ borderColor: active ? p.c : "var(--border)", background: active ? `${p.c}11` : "white" }}>
                    <input type="radio" name="pakket" defaultChecked={p.v === "Hi Groei"} onClick={() => setPakket(p.v)} className="mt-1 accent-foreground" />
                    <div>
                      <div className="font-extrabold text-lg" style={{ color: p.c }}>{p.v}</div>
                      <div className="text-sm text-muted-foreground">{p.d}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-extrabold mb-2" style={{ color: "var(--rb-violet)" }}>Kies de maat van het sportsetje</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Het officiële Hi Coach sportsetje is verplicht en wordt eenmalig extra in rekening gebracht bij inschrijving.
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
              {MATEN.map((m) => {
                const active = maat === m;
                return (
                  <label key={m} className={`cursor-pointer rounded-2xl border-2 p-4 text-center font-extrabold transition-all ${active ? "bg-foreground text-white border-foreground" : "border-border hover:bg-secondary"}`}>
                    <input type="radio" name="maat" className="sr-only" defaultChecked={m === "Maat 104/110"} onClick={() => setMaat(m)} />
                    {m}
                  </label>
                );
              })}
              <label className="rounded-2xl border-2 border-border p-2 transition-all focus-within:border-foreground">
                <input
                  type="text"
                  placeholder="Andere maat"
                  className="w-full px-3 py-2 rounded-xl bg-white font-extrabold text-center focus:outline-none"
                  onChange={(e) => setMaat(e.target.value || "Maat 104/110")}
                />
              </label>
            </div>
          </div>

          <div className="rounded-2xl bg-secondary p-6">
            <h2 className="font-extrabold">Bevestig je inschrijving</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Door je in te schrijven ga je akkoord met de termijnen en opzegtermijn die horen bij jouw gekozen pakket. Bij volledige bezetting word je automatisch op de wachtlijst geplaatst en nemen wij persoonlijk contact met je op.
            </p>
            <label className="mt-4 flex items-start gap-3 cursor-pointer">
              <input type="checkbox" required name="akkoord" className="mt-1 h-5 w-5 accent-rb-green" />
              <span className="font-bold">Ik ga akkoord met de voorwaarden.</span>
            </label>
          </div>

          <div>
            <button type="submit" disabled={sending} className="btn-cta w-full sm:w-auto disabled:opacity-60">
              {sending ? "Bezig met versturen..." : "Inschrijving bevestigen"}
            </button>
            {error && (
              <p className="mt-4 text-sm font-bold" style={{ color: "var(--rb-red)" }}>{error}</p>
            )}
            <p className="mt-4 text-sm text-muted-foreground">
              Heb je eerst nog een vraag? <a className="underline font-bold" href="https://wa.me/31626716754">App ons via WhatsApp</a>, we reageren snel.
            </p>
          </div>
        </form>
      </section>

      <section className="py-16 flex justify-center px-6">
        <div className="p-[4px] rounded-3xl gradient-rainbow shadow-2xl" style={{ maxWidth: 260 }}>
          <div className="rounded-[calc(1.5rem-4px)] overflow-hidden">
            <video
              className="w-full h-auto block"
              src={hijZegtKom.url}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
            />
          </div>
        </div>
      </section>
    </>
  );
}

function Inschrijven() {
  return (
    <Page>
      <InschrijvenContent />
    </Page>
  );
}
